/**
 * A pay cycle's shortfall, judged over the whole cycle rather than day by day.
 *
 * Someone rostered 38 h a week who worked 9 h on Monday and 6 h on Friday is
 * not 1.6 h short on Friday: Monday's extra 1.4 h makes up most of it. Payroll
 * pays the cycle, and the fortnightly time-in-lieu rule (HHNodeServer
 * `utils/periodExcess.js`) already counts worked + paid leave against the
 * rostered hours for the period. Covering each short day on its own therefore
 * took leave for hours the employee had already worked, and on fortnightly pay
 * banked the same hours back as time in lieu at submission.
 *
 * So the cycle is short by (short hours − extra hours), never below zero, and
 * that is covered from time in lieu first, then from the leave the employee
 * chooses, up to the rostered hours and no further (Rob, 2026-10-08).
 *
 * Public holidays are left out of both sides, as in `periodExcess.js`: they are
 * paid, and hours worked on one are paid as something else.
 */
import type { LeaveType } from '../services/leave';

export interface ShortfallDay {
  date: string;
  scheduled_hours: number;
  worked_hours: number;
  is_public_holiday: boolean;
  locked: boolean;
  /** Shortfall still open on the day, after leave already requested for it. */
  remaining: number;
}

export interface PeriodShortfall {
  /** Sum of every short day's open shortfall. */
  short_hours: number;
  /** Hours worked past the roster on other days (any day that is not a public holiday). */
  extra_hours: number;
  /** What the cycle is actually short: short − extra, never below zero. */
  net_hours: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function periodShortfall(days: ShortfallDay[]): PeriodShortfall {
  let short = 0;
  let extra = 0;
  for (const d of days) {
    if (d.is_public_holiday) continue;
    short += Math.max(0, d.remaining);
    extra += Math.max(0, d.worked_hours - d.scheduled_hours);
  }
  return {
    short_hours: round2(short),
    extra_hours: round2(extra),
    net_hours: round2(Math.max(0, short - extra)),
  };
}

/** Leave of one type on one day. */
export interface CoverPart {
  date: string;
  leave_type: LeaveType;
  hours: number;
  /** The day had no work recorded and this part takes all of its shortfall. */
  whole_day: boolean;
}

/**
 * Which days take the leave, and of which type.
 *
 * Days with no work recorded go first, so as many as possible are whole-day
 * leave, which needs no time window and cannot collide with a timesheet entry.
 * Then partly worked days. Date order within each. Time in lieu is spent first,
 * up to `tilAvailable`; the rest is `otherType`.
 */
export function planCover(
  days: ShortfallDay[],
  need: number,
  tilAvailable: number,
  otherType: LeaveType,
): CoverPart[] {
  const candidates = days
    .filter(d => !d.is_public_holiday && !d.locked && d.remaining > 0.01)
    .sort((a, b) => {
      const ac = a.worked_hours > 0 ? 1 : 0;
      const bc = b.worked_hours > 0 ? 1 : 0;
      return ac - bc || a.date.localeCompare(b.date);
    });

  const parts: CoverPart[] = [];
  let left = round2(need);
  let til = round2(Math.max(0, tilAvailable));
  for (const d of candidates) {
    if (left <= 0.01) break;
    const take = round2(Math.min(d.remaining, left));
    const tilPart = round2(Math.min(take, til));
    const otherPart = round2(take - tilPart);
    // Whole-day leave books the day's rostered hours, so only a day nobody has
    // touched (no work, no partial leave already asked for) takes it.
    const clean = d.worked_hours === 0 && Math.abs(take - d.scheduled_hours) < 0.01;
    if (tilPart > 0.01) {
      parts.push({ date: d.date, leave_type: 'time_in_lieu', hours: tilPart, whole_day: clean && otherPart <= 0.01 });
    }
    if (otherPart > 0.01) {
      parts.push({ date: d.date, leave_type: otherType, hours: otherPart, whole_day: clean && tilPart <= 0.01 });
    }
    til = round2(til - tilPart);
    left = round2(left - take);
  }
  return foldSmallParts(parts);
}

/** The server refuses a partial-day request under a quarter of an hour. */
export const MIN_REQUEST_HOURS = 0.25;

// A sliver of time in lieu (the last of the balance) or of the other leave
// (the last of the need) cannot be requested on its own. Folded into the other
// part on the same day when there is one, else dropped: rounding it up would
// take leave past the rostered hours.
function foldSmallParts(parts: CoverPart[]): CoverPart[] {
  const out: CoverPart[] = [];
  for (const p of parts) {
    if (p.whole_day || p.hours >= MIN_REQUEST_HOURS) { out.push(p); continue; }
    const sibling = parts.find(q => q !== p && q.date === p.date);
    if (sibling) {
      sibling.hours = round2(sibling.hours + p.hours);
      sibling.whole_day = false;
    }
  }
  return out.filter(p => p.whole_day || p.hours >= MIN_REQUEST_HOURS);
}

/** A free stretch of a rostered day, as `LeaveDaySegment` gives it. */
export interface FreeSegment {
  start_time: string;
  end_time: string;
  span_hours: number;
  hours: number;
}

export interface CoverWindow {
  leave_type: LeaveType;
  start_time: string;
  end_time: string;
  hours: number;
}

const toMin = (t: string) => {
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};
const toHHmm = (mins: number) => {
  const m = Math.max(0, Math.round(mins));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/**
 * Lay a day's leave parts into time windows, in order, through the free
 * stretches of the day.
 *
 * A stretch's paid hours are its wall-clock width scaled down for the unpaid
 * break (`hours / span_hours`), and the server prices a window the same way,
 * so a window covering h paid hours is h × span/hours wide.
 *
 * `taken` are windows already requested on the day (pending partial leave);
 * they are cut out of the stretches first so two requests never overlap.
 * Returns null when the stretches cannot hold what is asked.
 */
export function windowsForDay(
  segments: FreeSegment[],
  taken: { start_time: string; end_time: string }[],
  parts: { leave_type: LeaveType; hours: number }[],
): CoverWindow[] | null {
  // Free stretches as minute ranges, each with its paid-per-minute ratio.
  type Range = { start: number; end: number; ratio: number };
  let ranges: Range[] = segments
    .filter(s => s.hours > 0 && s.span_hours > 0)
    .map(s => ({ start: toMin(s.start_time), end: toMin(s.end_time), ratio: s.hours / s.span_hours }));
  for (const t of taken) {
    const ts = toMin(t.start_time);
    const te = toMin(t.end_time);
    ranges = ranges.flatMap(r => {
      if (te <= r.start || ts >= r.end) return [r];
      const out: Range[] = [];
      if (ts > r.start) out.push({ ...r, end: ts });
      if (te < r.end) out.push({ ...r, start: te });
      return out;
    });
  }

  const windows: CoverWindow[] = [];
  let i = 0;
  let cursor = ranges[0]?.start ?? 0;
  for (const part of parts) {
    let left = part.hours;
    while (left > 0.01) {
      const r = ranges[i];
      if (!r) return null;
      if (cursor < r.start) cursor = r.start;
      const freeMin = r.end - cursor;
      if (freeMin <= 0) { i += 1; continue; }
      const wantMin = (left * 60) / r.ratio;
      const useMin = Math.min(freeMin, wantMin);
      // Whole minutes, as the server stores them; the last few seconds of a
      // stretch are not worth a second request.
      const endMin = Math.min(r.end, Math.round(cursor + useMin));
      if (endMin <= cursor) { i += 1; continue; }
      const hours = round2(((endMin - cursor) * r.ratio) / 60);
      windows.push({ leave_type: part.leave_type, start_time: toHHmm(cursor), end_time: toHHmm(endMin), hours });
      left = round2(left - hours);
      cursor = endMin;
      if (cursor >= r.end) i += 1;
      // Rounding to the minute can leave a sliver; under a minute's pay is not a request.
      if (left < 0.02) break;
    }
  }
  return windows;
}
